import React from 'react';
import _ from 'lodash';
import * as Ramda from 'ramda';

import { useQuery } from '@tanstack/react-query';

import {
  IconButton,
  InputAdornment,
  Paper,
  Popper,
  Typography,
  styled,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

import { Cancel, CheckCircle, Close } from '@mui/icons-material';
import { PasswordField } from '../PasswordField/PasswordField';

import { getPasswordConstraints } from '../../../api/auth/auth.api';
import { getData } from '../../../utils/functional/fp';
import { validatePassword } from '../../../api/SignupService';

import type { PasswordConstraintsDto } from '../../../api/auth/auth.dto';

type CreatePasswordFieldProps = {
  validationToken: string;
  onValidationErrors?: (errors: PasswordConstraintsDto[]) => void;
} & React.ComponentPropsWithRef<typeof PasswordField>;

export const CreatePasswordField = React.forwardRef<
  HTMLDivElement,
  CreatePasswordFieldProps
>((props, ref) => {
  const infoButtonRef = React.useRef<HTMLButtonElement>(null);
  const constraintsQuery = useQuery({
    queryKey: ['password-constraints'],
    queryFn: () => getPasswordConstraints().then(getData),
    staleTime: Infinity,
  });

  const [isTouched, setIsTouched] = React.useState(false);

  const [activeErrorCodes, setActiveErrorCodes] = React.useState<string[]>([]);
  const isErrors = Boolean(activeErrorCodes.length);

  const validate = React.useMemo(
    () =>
      _.debounce((str: string) => {
        validatePassword({ token: props.validationToken }, { password: str })
          .then(() => {
            setActiveErrorCodes([]);
            props.onValidationErrors?.([]);
          })
          .catch((error) => {
            if (error.response?.data?.errors) {
              const codes = error.response.data.errors.map(Ramda.prop('code'));
              setActiveErrorCodes(codes);
              props.onValidationErrors?.(error.response?.data?.errors);
            }
          });
      }, 250),
    [props.validationToken]
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    props.onChange?.(e);

    validate(e.target.value);
    if (!isTouched) {
      setIsTouched(true);
    }
  };

  const [isConstraintsPanelOpen, setIsConstraintsPanelOpen] =
    React.useState<boolean>(false);

  const togglePanel = () => {
    setIsConstraintsPanelOpen((prev) => !prev);
  };

  return (
    <>
      <PasswordField
        ref={ref}
        {...props}
        error={props.error || isErrors}
        onChange={handleChange}
        InputProps={{
          ...props.InputProps,
          startAdornment: (
            <InputAdornment position="start">
              <IconButton
                ref={infoButtonRef}
                onClick={togglePanel}
                size="small"
              >
                <InfoIcon color={isErrors ? 'error' : 'primary'} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Popper
        open={isConstraintsPanelOpen}
        anchorEl={infoButtonRef.current}
        placement="right"
        sx={{ zIndex: 999 }}
      >
        <Paper
          sx={{
            padding: '4px 8px',
            maxWidth: '500px',
            maxHeight: '70vh',
            overflowY: 'auto',
          }}
        >
          <IconButton
            onClick={togglePanel}
            size="small"
            sx={{ position: 'absolute', top: '0', right: '0' }}
          >
            <Close />
          </IconButton>

          <ConstraintList>
            {constraintsQuery.data != null
              ? constraintsQuery.data?.map?.((constraint) => {
                  const isConstraintFailed = activeErrorCodes.some(
                    (code) => code == constraint.errorCode
                  );

                  return (
                    <ConstraintListItem>
                      <ConstraintIcon
                        isTouched={isTouched}
                        isFailed={isConstraintFailed}
                      />

                      <Typography component="span" fontSize="1rem">
                        {constraint.displayText}
                      </Typography>
                    </ConstraintListItem>
                  );
                })
              : null}
          </ConstraintList>
        </Paper>
      </Popper>
    </>
  );
});

const ConstraintList = styled('ul')`
  list-style: none;
  padding: 0;

  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ConstraintListItem = styled('li')`
  display: flex;
  align-items: center;

  gap: 0.7rem;
`;

const ConstraintIcon = ({
  isFailed,
  isTouched,
}: {
  isFailed: boolean;
  isTouched: boolean;
}) => {
  if (isFailed) {
    return <Cancel fontSize="small" color="error" />;
  }

  return (
    <CheckCircle fontSize="small" color={isTouched ? 'success' : 'inherit'} />
  );
};
